import { useState } from "react"
import { useNavigate, useParams } from "@tanstack/react-router"

import { AlertDialog } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { ArticleKindTarget } from "./article-kind"
import { useShortcut } from "@/lib"
import { analytics } from "@/lib/analytics"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { shouldRejectKeystrokes } from "../sidebar"
import { isLinkArticle } from "./article-kind"

export function useArticleDelete({
	id,
	title,
	kind,
	enableShortcut = false,
}: ArticleKindTarget & {
	id: string
	title: string
	// Sidebar rows call this hook once per row, so only the view that owns the
	// open article opts into the shortcut.
	enableShortcut?: boolean
}) {
	const [isDeleteOpen, setIsDeleteOpen] = useState(false)
	const navigate = useNavigate()
	const params = useParams({ strict: false })
	const zero = useZero()
	const { role } = useRole()

	const noun = isLinkArticle({ kind }) ? "link" : "article"
	const isOpenInEditor = params.id === id || params.articleId === id

	const dialog = (
		<AlertDialog
			isOpen={isDeleteOpen}
			onOpenChange={setIsDeleteOpen}
			confirmText="Delete"
			title={title ? `Delete "${title}"?` : `Delete this ${noun}?`}
			description={
				title
					? `Are you sure you want to delete the ${noun} "${title}"?`
					: `Are you sure you want to delete this ${noun}?`
			}
			onCancel={() => {
				analytics.track("cancel-delete-helpcenterarticle", { id })
				setIsDeleteOpen(false)
			}}
			onConfirm={async () => {
				setIsDeleteOpen(false)
				await mutateOk(zero.mutate(mutators.helpCenterArticles.delete({ id })))
				analytics.track("helpcenterarticle-delete", { id })
				if (isOpenInEditor) void navigate({ to: "/edit-docs" })
			}}
		/>
	)

	const requestDelete = () => {
		setIsDeleteOpen(true)
		analytics.track("open-delete-docs-dialog", { id })
	}

	useShortcut(
		"meta+backspace",
		(e) => {
			e.preventDefault()
			e.stopPropagation()
			requestDelete()
		},
		{
			enabled: enableShortcut && role !== "VIEWER",
			enableOnContentEditable: true,
			enableOnFormTags: true,
			skipDefaultGuards: true,
			ignoreEventWhen: () => shouldRejectKeystrokes() ?? false,
		},
	)

	return { dialog, requestDelete }
}
